import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import PayerConnections from '@features/admin-v2/views/payer-connections';
import { useDebounce } from '@hooks/useDebounce';
import { Alert, LinearProgress } from '@mui/material';

import { EhrFleetSummaryProvider } from './components/ehr/EhrFleetSummary';
import { type FleetTrack, StaffIntakeFleet } from './components/StaffIntakeFleet';
import { STAFF_INTAKE_TABS, StaffIntakeStatusShell } from './components/StaffIntakeStatusShell';
import { useGetAdminIntakeFleetQuery } from './store/api';
import { AdminIntakeVerificationQueue } from './AdminIntakeVerificationQueue';

function FleetContent({ track, canVerify }: { track: FleetTrack; canVerify: boolean }) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const prefix =
    track === 'overview' ? 'overview' : track === 'credentialing' ? 'credentialing' : 'ehr';
  const search = (params.get(`${prefix}Search`) ?? '').slice(0, 100);
  const debounced = useDebounce(search, 300);
  const requestedPage = Number(params.get(`${prefix}Page`) ?? 1);
  const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const query = useGetAdminIntakeFleetQuery(
    { search: debounced, page, perPage: 20 },
    { refetchOnMountOrArgChange: true, refetchOnFocus: true, refetchOnReconnect: true },
  );
  const update = (key: string, value: string) =>
    setParams(
      previous => {
        const next = new URLSearchParams(previous);
        next.set(`${prefix}${key}`, value);
        if (key === 'Search') next.set(`${prefix}Page`, '1');
        return next;
      },
      { replace: true },
    );
  return (
    <EhrFleetSummaryProvider
      organizationIds={
        search === debounced
          ? (query.currentData?.items.map(item => item.organization_id) ?? [])
          : []
      }
      revision={query.fulfilledTimeStamp}
      enabled={track !== 'credentialing'}
    >
      <StaffIntakeFleet
        track={track}
        canVerify={canVerify}
        data={search === debounced ? query.currentData : undefined}
        loading={query.isFetching || search !== debounced}
        failed={query.isError}
        search={search}
        page={page}
        perPage={20}
        returnTo={location.pathname + location.search}
        onSearch={value => update('Search', value)}
        onPage={value => update('Page', String(value))}
        onRefresh={() => void query.refetch()}
      />
    </EhrFleetSummaryProvider>
  );
}

export function StaffIntakeStatusPage() {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const roles: unknown = user?.['https://api.smartscribe.health/roles'];
  const authorized =
    isAuthenticated &&
    Array.isArray(roles) &&
    roles.some(role => role === 'Admin' || role === 'Moderator');
  if (isLoading) return <LinearProgress aria-label="Loading staff access" />;
  if (!authorized) return <Alert severity="error">Staff access is required.</Alert>;
  return (
    <ScopedStaffIntakeStatusPage
      key={JSON.stringify([user?.sub, roles])}
      admin={Array.isArray(roles) && roles.includes('Admin')}
    />
  );
}

function ScopedStaffIntakeStatusPage({ admin }: { admin: boolean }) {
  const [params, setParams] = useSearchParams();
  const selected =
    STAFF_INTAKE_TABS.find(tab => tab.value === params.get('statusTab'))?.value ?? 'overview';
  return (
    <StaffIntakeStatusShell
      selected={selected}
      onSelect={value =>
        setParams(previous => {
          const next = new URLSearchParams(previous);
          next.set('statusTab', value);
          return next;
        })
      }
    >
      {selected === 'verification' ? (
        admin ? (
          <AdminIntakeVerificationQueue />
        ) : (
          <Alert severity="info">
            Verification review requires Admin access. Billing and recorded credentialing data
            remain available.
          </Alert>
        )
      ) : selected === 'billing' ? (
        <PayerConnections embedded />
      ) : (
        <FleetContent key={selected} track={selected} canVerify={admin} />
      )}
    </StaffIntakeStatusShell>
  );
}
